import { FC } from "react";
import { sanitizeHtml } from "@1-now/booking-core";
import PhoneInput from "react-phone-input-2";
import { Checkbox } from "@headlessui/react";
import { CheckIcon } from "@heroicons/react/24/outline";

export interface RenterField {
  value: string;
  error?: string;
  onChange: (value: string) => void;
  /** 1NOW-10770: "Use a different email" brings the renter back here, to this field. */
  autoFocus?: boolean;
}

const inputCls =
  "h-12 w-full rounded-xl border border-secondary-100 bg-transparent px-3 text-base text-text-color focus:border-text-color focus:ring-0";

const Field: FC<{ label: string; field: RenterField; type?: string; autoComplete?: string; name: string }> = ({
  label,
  field,
  type = "text",
  autoComplete,
  name,
}) => (
  <label className="block min-w-0">
    <span className="mb-1 block text-xs font-bold">{label}</span>
    <input
      className={inputCls}
      type={type}
      name={name}
      autoComplete={autoComplete}
      value={field.value}
      onChange={(e) => field.onChange(e.target.value)}
      aria-invalid={Boolean(field.error)}
      autoFocus={field.autoFocus}
    />
    {field.error && <span className="mt-1 block text-xs font-semibold text-red-500">{field.error}</span>}
  </label>
);

/**
 * Step 1 of the booking: "Who will drive?" (1NOW-11155). The fields are
 * the page's own state and rules (validateForm, the confirmation checkbox),
 * passed in; nothing is validated here.
 */
const DetailsStep: FC<{
  fields: { firstName: RenterField; lastName: RenterField; email: RenterField; phone: RenterField };
  /** The operator's confirmation checkbox, when they require it. */
  confirm?: { checked: boolean; onChange: (checked: boolean) => void; html?: string; companyName?: string };
  /** Desktop (1NOW-11173): two columns, the names first, then the email and the phone. */
  wide?: boolean;
}> = ({ fields, confirm, wide }) => (
  <div>
    <div className={wide ? "grid grid-cols-2 gap-x-5 gap-y-4" : "flex flex-col gap-3"}>
      {wide ? (
        <>
          <Field label="First name" name="firstName" autoComplete="given-name" field={fields.firstName} />
          <Field label="Last name" name="lastName" autoComplete="family-name" field={fields.lastName} />
        </>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name" name="firstName" autoComplete="given-name" field={fields.firstName} />
          <Field label="Last name" name="lastName" autoComplete="family-name" field={fields.lastName} />
        </div>
      )}
      <Field label="Email" name="email" type="email" autoComplete="email" field={fields.email} />
      <div>
        <span className="mb-1 block text-xs font-bold">Phone number</span>
        <div className="rounded-xl border border-secondary-100 px-1">
          <PhoneInput
            country={"us"}
            value={fields.phone.value}
            onChange={(v) => fields.phone.onChange(v)}
            inputProps={{ name: "phone", required: true, autoFocus: false }}
            inputStyle={{
              border: "none",
              width: "100%",
              minHeight: "46px",
              boxShadow: "none",
              background: "transparent",
              fontSize: "16px",
            }}
            buttonStyle={{ background: "transparent", border: "none" }}
          />
        </div>
        {fields.phone.error && (
          <span className="mt-1 block text-xs font-semibold text-red-500">{fields.phone.error}</span>
        )}
      </div>
    </div>

    {confirm && (
      <div className="mt-4 flex items-start gap-3">
        <Checkbox
          checked={confirm.checked}
          onChange={confirm.onChange}
          aria-label="I confirm my details are correct"
          className="group mt-0.5 size-7 shrink-0 rounded-md p-1 ring-1 ring-inset ring-primary-brand data-[checked]:bg-primary-brand data-[checked]:bg-opacity-20"
        >
          <CheckIcon className={`size-5 stroke-primary-brand ${confirm.checked ? "visible" : "invisible"}`} />
        </Checkbox>
        {confirm.html ? (
          <span
            className="policy-checkbox-copy text-sm"
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(confirm.html.replace(/\{\{\s*company\s*name\s*\}\}/gi, confirm.companyName ?? "")),
            }}
          />
        ) : (
          <span className="text-sm">
            I confirm that my renter details are correct and I agree to receive communication from{" "}
            {confirm.companyName} about my rental booking, service updates, and related offers.
          </span>
        )}
      </div>
    )}
  </div>
);

export default DetailsStep;
